/**
 * ForwardPickerModal — TG-style выбор чата для пересылки.
 * Группирует топики по отделам, отдельная секция для DM. Использует реальные
 * аватарки (uploaded image → emoji icon → цветной gradient с инициалом).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Icon } from "@/components/prototype/Icon";
import { useBackdropClose } from "@/lib/backdropClose";
import {
  forwardChatMessages,
  fetchActiveUsers,
  getOrCreateDm,
  type UserShort,
  topicAvatarUrl,
  userAvatarUrl,
  chatAttachmentPreviewUrl,
  chatVideoThumbnailUrl,
  type ChatTopic,
} from "@/api/dashboard";
import { parseTopicIcon } from "@/data/animatedIcons";
import { useAuth } from "@/auth/AuthContext";

/** Что пересылаем — для превью в окне выбора и в плашке над строкой ввода. */
export interface ForwardPreviewInfo {
  count: number;
  author: string;
  text: string;
  attachments: Array<{ id: number; mime: string | null; filename: string; size_bytes?: number | null }>;
  /** По записи на каждое пересылаемое сообщение — для списка предпросмотров. */
  items: Array<{
    author: string;
    text: string;
    attachments: Array<{ id: number; mime: string | null; filename: string; size_bytes?: number | null }>;
  }>;
}

interface Props {
  topics: ChatTopic[];
  sourceMessageIds: number[];
  sourceAttachmentIds?: number[];
  /** Топик, который надо скрыть из списка целей (по умолчанию не скрываем никого). */
  excludeTopicId?: number;
  /** Чат, из которого пересылают: остаётся в списке (переслать можно и в него же),
   *  помечается «этот чат» (29.09.2026). */
  currentTopicId?: number;
  onClose: () => void;
  /** Колбэк после успешной пересылки — для перехода в target-чат (опц). */
  onSent?: (targetTopicId: number) => void;
  /** Превью пересылаемого вверху окна (автор, текст, плитки вложений). */
  preview?: ForwardPreviewInfo | null;
  /** Выбран ОДИН чат: не отправляем сразу, а открываем его с плашкой «Переслать»
   *  над строкой ввода — отправка по кнопке «Отправить», как в TG (18.09.2026). */
  onPickSingle?: (targetTopicId: number) => void;
}

const DEPT_LABELS: Record<string, string> = {
  sales: "Отдел продаж",
  production: "Производство",
  marketing: "Маркетинг",
  admin: "Администрация",
  finance: "Финансы",
  supply: "Снабжение",
};

const TG_AVATAR_GRADIENTS = [
  "linear-gradient(135deg, #ff885e 0%, #ff516a 100%)",
  "linear-gradient(135deg, #ffcd6a 0%, #ffa85c 100%)",
  "linear-gradient(135deg, #82b1ff 0%, #665fff 100%)",
  "linear-gradient(135deg, #a0de7e 0%, #54cb68 100%)",
  "linear-gradient(135deg, #53edd6 0%, #28c9b7 100%)",
  "linear-gradient(135deg, #72d5fd 0%, #2a9ef1 100%)",
  "linear-gradient(135deg, #e0a2f3 0%, #d669ed 100%)",
  "linear-gradient(135deg, #e85c5c 0%, #ee8585 100%)",
];
function pickGradient(key: string | number): string {
  const k = String(key);
  let h = 0;
  for (let i = 0; i < k.length; i++) h = (h * 31 + k.charCodeAt(i)) >>> 0;
  return TG_AVATAR_GRADIENTS[h % TG_AVATAR_GRADIENTS.length];
}

function TopicAvatar({ topic, size = 40 }: { topic: ChatTopic; size?: number }) {
  // 0) DM — приоритет аватарке партнёра (его avatar_filename + userAvatarUrl)
  if (topic.kind === "dm" && topic.dm_partner) {
    const p = topic.dm_partner;
    if (p.avatar_filename) {
      const url = userAvatarUrl(p.user_id, p.avatar_filename);
      if (url) {
        return (
          <img
            src={url}
            alt=""
            loading="lazy"
            style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
          />
        );
      }
    }
    // Fallback для DM — gradient + инициал по имени партнёра
    const initial = (p.display_name ?? topic.name ?? "?").trim().charAt(0).toUpperCase() || "?";
    return (
      <div
        style={{
          width: size, height: size, borderRadius: "50%",
          background: pickGradient(p.user_id),
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          color: "#fff", fontWeight: 600, fontSize: Math.round(size * 0.4), flexShrink: 0,
        }}
      >
        {initial}
      </div>
    );
  }
  // 1) Загруженная картинка топика
  if (topic.avatar_filename) {
    const url = topicAvatarUrl(topic.id, topic.avatar_filename);
    if (url) {
      return (
        <img
          src={url}
          alt=""
          loading="lazy"
          style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
        />
      );
    }
  }
  // 2) Emoji-иконка
  if (topic.icon) {
    const parsed = parseTopicIcon(topic.icon);
    if (parsed.kind === "emoji") {
      return (
        <div
          style={{
            width: size, height: size, borderRadius: "50%",
            background: pickGradient(topic.id),
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontSize: Math.round(size * 0.55), lineHeight: 1, flexShrink: 0,
          }}
        >
          {parsed.emoji}
        </div>
      );
    }
    if (parsed.kind === "pack") {
      return (
        <img
          src={parsed.url}
          alt=""
          width={size}
          height={size}
          loading="lazy"
          style={{ width: size, height: size, objectFit: "contain", borderRadius: "50%", flexShrink: 0 }}
        />
      );
    }
  }
  // 3) Fallback — gradient + инициал
  const initial = (topic.name ?? "?").trim().charAt(0).toUpperCase() || "?";
  return (
    <div
      style={{
        width: size, height: size, borderRadius: "50%",
        background: pickGradient(topic.id),
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        color: "#fff", fontWeight: 600, fontSize: Math.round(size * 0.4), flexShrink: 0,
      }}
    >
      {initial}
    </div>
  );
}

export function ForwardPickerModal({
  topics, sourceMessageIds, sourceAttachmentIds = [], excludeTopicId, currentTopicId, onClose, onSent,
  preview, onPickSingle,
}: Props) {
  const backdrop = useBackdropClose(onClose);
  const [query, setQuery] = useState("");
  // Мульти-выбор: Set<topicId>. Клик — toggle.
  const [selectedSet, setSelectedSet] = useState<Set<number>>(new Set());
  // Защита от «призрачного» касания (18.09.2026): окно открывается под пальцем,
  // и отпускание/догоняющий click в первые мгновения молча отмечал чат под ним —
  // так пересылка могла уйти лишнему получателю. Первые 400 мс выбор игнорируем.
  const openedAtRef = useRef(Date.now());

  // Люди из общих организаций, с кем ещё нет личного чата (03.10.2026): строка-заготовка с
  // отрицательным id; сам личный чат создаётся только в момент пересылки (resolveTargets).
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const contactsQ = useQuery({ queryKey: ["fwd/contacts"], queryFn: fetchActiveUsers, staleTime: 60_000 });
  const { contactTopics, contactUser, orgOf } = useMemo(() => {
    type Contact = UserShort & { role?: string | null; org?: string | null };
    const list = (contactsQ.data ?? []) as Contact[];
    const withDm = new Set(topics.filter(t => t.kind === "dm" && t.dm_partner?.user_id).map(t => t.dm_partner!.user_id));
    const contactUser = new Map<number, string>();
    const orgOf = new Map<string, string>();
    const contactTopics: ChatTopic[] = [];
    list.forEach((u, i) => {
      if (u.org) orgOf.set(u.id, u.org);
      if (u.id === me?.id || u.role === "bot" || withDm.has(u.id)) return;
      const fakeId = -(i + 1);
      contactUser.set(fakeId, u.id);
      contactTopics.push({
        id: fakeId, slug: "", name: u.display_name ?? "", icon: null, sort_order: 0,
        last_message: null, unread_count: 0, kind: "dm",
        dm_partner: {
          user_id: u.id, display_name: u.display_name, avatar_filename: u.avatar_filename ?? null,
          departments: u.departments ?? [], position: u.position, role: u.role ?? null,
        },
      });
    });
    return { contactTopics, contactUser, orgOf };
  }, [contactsQ.data, topics, me?.id]);

  /** Отрицательный id — человек без личного чата: создать чат и вернуть настоящий id. */
  const resolveTargets = async (ids: number[]): Promise<number[]> => {
    const out: number[] = [];
    let created = false;
    for (const id of ids) {
      if (id > 0) { out.push(id); continue; }
      const uid = contactUser.get(id);
      if (!uid) continue;
      const t = await getOrCreateDm(uid);
      out.push(t.id);
      created = true;
    }
    if (created) await qc.invalidateQueries({ queryKey: ["chat/topics"] });
    return out;
  };
  const pickSingle = (id: number) => {
    if (!onPickSingle) return;
    if (id > 0) { onPickSingle(id); return; }
    resolveTargets([id])
      .then(([real]) => { if (real) onPickSingle(real); })
      .catch((e: Error) => alert(`Не удалось открыть чат: ${e.message}`));
  };

  const toggleSelected = (id: number) => {
    if (Date.now() - openedAtRef.current < 400) return;
    setSelectedSet(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Фильтруем разрешённые типы (исключаем group/drive/tasks topics)
  // + дедуп DM по partner.user_id: в БД накопились дубликаты DM-топиков
  // с одним и тем же партнёром (несколько записей с kind='dm' указывающих на
  // того же юзера → в UI это «3 Геннадия подряд»). Оставляем самый свежий
  // (с наибольшим id — обычно самый активный).
  const filterable = useMemo(() => {
    const allowed = [...topics, ...contactTopics].filter(t => {
      if (t.kind === "drive" || t.kind === "tasks" || t.kind === "group") return false;
      if (excludeTopicId != null && t.id === excludeTopicId) return false;
      return true;
    });
    const dmSeen = new Map<string, typeof allowed[number]>();
    const result: typeof allowed = [];
    for (const t of allowed) {
      if (t.kind === "dm" && t.dm_partner?.user_id) {
        const key = t.dm_partner.user_id;
        const prev = dmSeen.get(key);
        if (!prev || t.id > prev.id) {
          dmSeen.set(key, t);
        }
      } else {
        result.push(t);
      }
    }
    result.push(...dmSeen.values());
    return result;
  }, [topics, contactTopics, excludeTopicId]);

  // Поиск по имени
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return filterable;
    return filterable.filter(t => {
      // Для DM — ищем по имени партнёра (topic.name бажный у DM)
      const name = t.kind === "dm" && t.dm_partner?.display_name
        ? t.dm_partner.display_name
        : (t.name ?? "");
      return name.toLowerCase().includes(q);
    });
  }, [filterable, query]);

  // Группировка по отделу. Для топиков-чатов — t.department.
  // Для DM — берём отдел партнёра (dm_partner.departments[0]).
  // Боты / DM без отдела → секция «Личные сообщения / Боты».
  const groups = useMemo(() => {
    const map = new Map<string, ChatTopic[]>();
    for (const t of visible) {
      let key: string;
      if (t.kind === "dm") {
        const partner = t.dm_partner;
        const partnerRole = partner?.role;
        if (partnerRole === "bot") {
          key = "_bots";
        } else {
          const depts = (partner?.departments ?? []) as string[];
          // Если партнёр в нескольких отделах — берём первый «известный»
          const knownOrder = ["sales", "production", "marketing", "admin", "finance", "supply"];
          let pickedDept: string | null = null;
          for (const k of knownOrder) {
            if (depts.includes(k)) { pickedDept = k; break; }
          }
          if (!pickedDept && depts.length > 0) pickedDept = depts[0];
          const org = partner?.user_id ? orgOf.get(partner.user_id) : undefined;
          key = pickedDept ? `dm:${pickedDept}` : org ? `org:${org}` : "_dm_other";
        }
      } else if (t.kind === "favorites") {
        key = "_fav";
      } else {
        key = t.department || "_other";
      }
      const arr = map.get(key) ?? [];
      arr.push(t);
      map.set(key, arr);
    }
    // Сортировка внутри группы по имени
    // (по ПОКАЗАННОМУ имени: у личных чатов topic.name бывает «своим»)
    const shown = (t: ChatTopic) => (t.kind === "dm" && t.dm_partner?.display_name ? t.dm_partner.display_name : t.name) ?? "";
    for (const arr of map.values()) {
      arr.sort((a, b) => shown(a).localeCompare(shown(b), "ru"));
    }
    // Порядок секций (29.09.2026): «Избранное» → ЛЮДИ (личные по отделам, затем без
    // отдела) → чаты и группы по отделам → прочие чаты → боты.
    const order = ["sales", "production", "marketing", "admin", "finance", "supply"];
    const ordered: { key: string; label: string; topics: ChatTopic[] }[] = [];
    if (map.has("_fav")) ordered.push({ key: "_fav", label: "Избранное", topics: map.get("_fav")! });
    for (const k of order) {
      const dmKey = `dm:${k}`;
      if (map.has(dmKey)) ordered.push({ key: dmKey, label: `Люди · ${DEPT_LABELS[k] ?? k}`, topics: map.get(dmKey)! });
    }
    for (const [k, arr] of map.entries()) {
      if (!k.startsWith("dm:")) continue;
      const deptKey = k.slice(3);
      if (order.includes(deptKey)) continue;
      ordered.push({ key: k, label: `Люди · ${DEPT_LABELS[deptKey] ?? deptKey}`, topics: arr });
    }
    for (const [k, arr] of map.entries()) {
      if (k.startsWith("org:")) ordered.push({ key: k, label: `Люди · ${k.slice(4)}`, topics: arr });
    }
    if (map.has("_dm_other")) ordered.push({ key: "_dm_other", label: "Люди · без отдела", topics: map.get("_dm_other")! });
    for (const k of order) {
      if (map.has(k)) ordered.push({ key: k, label: `Чаты · ${DEPT_LABELS[k] ?? k}`, topics: map.get(k)! });
    }
    for (const [k, arr] of map.entries()) {
      if (order.includes(k)) continue;
      if (k.startsWith("dm:") || k.startsWith("org:") || k === "_dm_other" || k === "_bots" || k === "_other" || k === "_fav") continue;
      ordered.push({ key: k, label: `Чаты · ${DEPT_LABELS[k] ?? k}`, topics: arr });
    }
    if (map.has("_other")) ordered.push({ key: "_other", label: "Чаты · без отдела", topics: map.get("_other")! });
    if (map.has("_bots")) ordered.push({ key: "_bots", label: "Боты", topics: map.get("_bots")! });
    return ordered;
  }, [visible, orgOf]);

  // Переслать сразу в несколько чатов (за один POST на каждый chat)
  const sendMut = useMutation({
    mutationFn: async (picked: number[]) => {
      const targetIds = await resolveTargets(picked);
      const results = await Promise.allSettled(
        targetIds.map(id => forwardChatMessages(id, sourceMessageIds, sourceAttachmentIds))
      );
      const failed = results.filter(r => r.status === "rejected").length;
      if (failed > 0 && failed === targetIds.length) {
        throw new Error("Все цели отказали");
      }
      return targetIds;
    },
    onSuccess: (targetIds) => {
      onClose();
      // Если переслали только в один — переходим в него
      if (targetIds.length === 1) onSent?.(targetIds[0]);
    },
    onError: (e: Error) => alert(`Не удалось переслать: ${e.message}`),
  });

  const totalCount = sourceMessageIds.length + sourceAttachmentIds.length;

  return (
    <div className="fwd-bg" {...backdrop}>
      <div className="fwd-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fwd-header">
          <div className="fwd-title">
            Переслать {totalCount > 1 ? `${totalCount} сообщ.` : "сообщение"}
          </div>
          <button type="button" className="fwd-close" onClick={onClose} aria-label="Закрыть">
            <Icon name="x" size={18}/>
          </button>
        </div>

        {preview && preview.items.length <= 1 && (
          <div
            className="fwd-preview"
            style={{
              display: "flex", alignItems: "center", gap: 10, minWidth: 0,
              margin: "10px 18px 0", padding: "8px 10px", borderRadius: 8,
              borderLeft: "3px solid #1a73e8", background: "rgba(26,115,232,0.08)",
            }}
          >
            {preview.attachments.slice(0, 3).map(att => {
              const mime = (att.mime || "").toLowerCase();
              const img = mime.startsWith("image/");
              const vid = mime.startsWith("video/");
              return (
                <span
                  key={att.id}
                  title={att.filename}
                  style={{
                    flex: "0 0 auto", width: 44, height: 44, borderRadius: 6, overflow: "hidden",
                    background: "rgba(0,0,0,0.08)", display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  {img && <img src={chatAttachmentPreviewUrl(att.id)} alt="" draggable={false} style={{ width: "100%", height: "100%", objectFit: "cover" }}/>}
                  {vid && <img src={chatVideoThumbnailUrl(att.id)} alt="" draggable={false} style={{ width: "100%", height: "100%", objectFit: "cover" }}/>}
                  {!img && !vid && <Icon name="file" size={20}/>}
                </span>
              );
            })}
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#1a73e8", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {preview.author}
              </div>
              <div style={{ fontSize: 13, opacity: 0.85, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {preview.text || "—"}
              </div>
            </div>
          </div>
        )}
        {preview && preview.items.length > 1 && (
          <div
            className="fwd-preview is-multi"
            style={{
              display: "flex", flexDirection: "column", gap: 4, minWidth: 0,
              margin: "10px 18px 0", padding: "8px 10px", borderRadius: 8, maxHeight: 132, overflowY: "auto",
              borderLeft: "3px solid #1a73e8", background: "rgba(26,115,232,0.08)",
            }}
          >
            {preview.items.slice(0, 4).map((it, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0, fontSize: 13 }}>
                {it.attachments.slice(0, 2).map(att => {
                  const mime = (att.mime || "").toLowerCase();
                  const img = mime.startsWith("image/");
                  const vid = mime.startsWith("video/");
                  return (
                    <span key={att.id} title={att.filename} style={{ flex: "0 0 auto", width: 28, height: 28, borderRadius: 5, overflow: "hidden", background: "rgba(0,0,0,0.08)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {img && <img src={chatAttachmentPreviewUrl(att.id)} alt="" draggable={false} style={{ width: "100%", height: "100%", objectFit: "cover" }}/>}
                      {vid && <img src={chatVideoThumbnailUrl(att.id)} alt="" draggable={false} style={{ width: "100%", height: "100%", objectFit: "cover" }}/>}
                      {!img && !vid && <Icon name="file" size={14}/>}
                    </span>
                  );
                })}
                <span style={{ minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  <span style={{ fontWeight: 600, color: "#1a73e8" }}>{it.author}:</span>{" "}
                  <span style={{ opacity: 0.85 }}>{it.text || "—"}</span>
                </span>
              </div>
            ))}
            {preview.items.length > 4 && (
              <div style={{ fontSize: 12, opacity: 0.7 }}>и ещё {preview.items.length - 4}</div>
            )}
          </div>
        )}

        <input
          type="search"
          className="fwd-search"
          placeholder="Поиск чата или человека…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />

        <div className="fwd-list">
          {groups.length === 0 && (
            <div className="fwd-empty">Ничего не найдено</div>
          )}
          {groups.map(group => (
            <div key={group.key} className="fwd-group">
              <div className="fwd-group-label">{group.label}</div>
              {group.topics.map(t => {
                const isSelected = selectedSet.has(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    className={`fwd-item${isSelected ? " is-selected" : ""}`}
                    onClick={() => toggleSelected(t.id)}
                    onDoubleClick={() => {
                      if (onPickSingle) { pickSingle(t.id); return; }
                      setSelectedSet(new Set([t.id])); sendMut.mutate([t.id]);
                    }}
                  >
                    <TopicAvatar topic={t} size={40}/>
                    <div className="fwd-item-body">
                      {/* Для DM в БД topic.name часто стоит «своё» имя (баг данных
                          накопился). Берём имя партнёра, чтобы не получалось «3 Геннадия». */}
                      <div className="fwd-item-name">
                        {t.kind === "dm" && t.dm_partner?.display_name
                          ? t.dm_partner.display_name
                          : t.name}
                      </div>
                      {(t.kind === "dm" || t.id === currentTopicId) && (
                        <div className="fwd-item-dept">
                          {[t.kind === "dm" ? (t.id < 0 ? "новый личный чат" : "личный чат") : "", t.id === currentTopicId ? "этот чат" : ""].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </div>
                    <div className={`fwd-checkbox${isSelected ? " is-on" : ""}`} aria-hidden="true">
                      {isSelected && <Icon name="check" size={14}/>}
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="fwd-footer">
          <button type="button" className="fwd-btn-secondary" onClick={onClose}>Отмена</button>
          <button
            type="button"
            className="fwd-btn-primary"
            disabled={selectedSet.size === 0 || sendMut.isPending}
            onClick={() => {
              if (selectedSet.size === 0) return;
              const ids = Array.from(selectedSet);
              // Один чат → в него, с плашкой и подтверждением «Отправить» (TG).
              if (ids.length === 1 && onPickSingle) { pickSingle(ids[0]); return; }
              sendMut.mutate(ids);
            }}
          >
            {sendMut.isPending
              ? "Отправка…"
              : selectedSet.size > 1
                ? `Переслать в ${selectedSet.size}`
                : (onPickSingle ? "Далее" : "Переслать")}
          </button>
        </div>
      </div>
    </div>
  );
}
